import { authFetch, getApiBase, tokenStore } from "./auth";

export interface SiggeCase {
  id: number;
  title: string;
  status: string;
  client: { full_name: string; email: string } | null;
}

function requireAuth(): void {
  if (!tokenStore.access) throw new Error("Inte inloggad mot Sigge.");
}

async function readError(res: Response): Promise<string> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      const data = (await res.json()) as { detail?: string };
      if (data?.detail) return data.detail;
    } catch {
      /* fall through */
    }
  }
  return `HTTP ${res.status}`;
}

export async function searchCases(query: string): Promise<SiggeCase[]> {
  requireAuth();
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  const res = await authFetch(`${getApiBase()}/api/cases/?${params}`);
  if (!res.ok) throw new Error(await readError(res));
  const data = (await res.json()) as { results: SiggeCase[] };
  return data.results;
}

export interface CaseSuggestion {
  case: SiggeCase;
  score: number;
  reasons: string[];
}

export interface AlreadyArchived {
  case_id: number;
  case_title: string;
  email_id: number;
}

export interface SuggestResponse {
  already_archived: AlreadyArchived | null;
  suggestions: CaseSuggestion[];
}

export async function suggestCases(params: {
  senderEmail: string;
  recipients: string[];
  outlookConversationId: string;
  outlookMessageId: string;
  subject: string;
}): Promise<SuggestResponse> {
  const q = new URLSearchParams();
  if (params.senderEmail) q.set("sender_email", params.senderEmail);
  if (params.outlookConversationId)
    q.set("outlook_conversation_id", params.outlookConversationId);
  if (params.outlookMessageId)
    q.set("outlook_message_id", params.outlookMessageId);
  if (params.subject) q.set("subject", params.subject);
  for (const r of params.recipients) {
    if (r) q.append("recipient", r);
  }

  requireAuth();
  const res = await authFetch(`${getApiBase()}/api/emails/suggest/?${q}`);
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as SuggestResponse;
}

export interface FilingCase {
  id: number;
  title: string;
  status: string;
  client_email: string;
  client_name: string;
  customer_name: string;
  updated_at: string;
}

export async function fetchCasesForFiling(): Promise<FilingCase[]> {
  requireAuth();
  const res = await authFetch(`${getApiBase()}/api/cases/for-filing/`);
  if (!res.ok) throw new Error(await readError(res));
  const data = (await res.json()) as { results: FilingCase[] };
  return data.results;
}

/** Konverterar en cachad FilingCase till SiggeCase-formen som UI:t använder. */
export function filingCaseToSigge(c: FilingCase): SiggeCase {
  return {
    id: c.id,
    title: c.title,
    status: c.status,
    client: c.client_email || c.client_name
      ? { email: c.client_email, full_name: c.client_name }
      : null,
  };
}

export interface ImportEmailPayload {
  case: number;
  direction: "inbound" | "outbound";
  subject: string;
  sender_email: string;
  sender_name: string;
  recipients: { email: string; name: string }[];
  cc: { email: string; name: string }[];
  sent_at: string | null;
  body_text: string;
  body_html: string;
  outlook_message_id: string;
  outlook_conversation_id: string;
  /**
   * Stage 7 — user-val vid save. `save_only` sparar mejlet + bilagor
   * utan analys. `ingest` schemalägger full pipeline. `auto` låter
   * legal-klassificeraren avgöra per bilaga. Backend routar
   * ingestion_mode till dokument-orkestratorn för attachment→Document-
   * konvertering (framtida arbete); mejlraden själv sparas alltid.
   */
  ingestion_mode: "ingest" | "save_only" | "auto";
}

export interface AttachmentBlob {
  name: string;
  contentType: string;
  blob: Blob;
}

export async function importEmail(
  payload: ImportEmailPayload,
  attachments: AttachmentBlob[]
): Promise<{ id: number }> {
  const fd = new FormData();
  fd.append("payload", JSON.stringify(payload));
  attachments.forEach((a, i) => {
    fd.append(`attachment_${i}`, a.blob, a.name);
    fd.append(`attachment_${i}_name`, a.name);
  });

  requireAuth();
  const res = await authFetch(`${getApiBase()}/api/emails/import/`, {
    method: "POST",
    body: fd,
  });

  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as { id: number };
}

export type ActivityType =
  | "radgivning"
  | "dokgranskning"
  | "upprattande"
  | "mote"
  | "ovrigt";

export const ACTIVITY_LABELS: Record<ActivityType, string> = {
  radgivning: "Rådgivning",
  dokgranskning: "Dokumentgranskning",
  upprattande: "Upprättande",
  mote: "Möte",
  ovrigt: "Övrigt",
};

export interface TimeEntryPayload {
  case: number;
  activity_type: ActivityType;
  duration_minutes: number;
  note?: string;
  billable?: boolean;
}

export async function logTime(
  payload: TimeEntryPayload
): Promise<{ id: number }> {
  requireAuth();
  const res = await authFetch(`${getApiBase()}/api/time-entries/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as { id: number };
}

export const REASON_LABELS: Record<string, string> = {
  conversation_match: "Samma mejltråd",
  client_email_match: "Klientens adress",
  customer_contact_match: "Kundkontakt",
  recipient_client_match: "Mottagare är klient",
  personal_history: "Tidigare arkiverat",
  owner: "Du är ansvarig",
  team: "Du är i teamet",
};
